import React from 'react'
import { GetServerSideProps, NextPage } from 'next'
import { readToken } from '../../lib/sanity.api'

import {
  getRegionBySlug,
  getClient,
  getGetAllProductsInLocation,
  getGetAllCitiesInRegion,
  getGetAllCategoriesInRegion,
} from '../../lib/sanity.client'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { PageBanner } from '../../components/PageBanner'
import PageHead from '../../components/shared/PageHead'
import { Description } from '../../components/shared/Description'
import { FAQ } from '../../components/shared/FAQ'
import TileSlider from '../../components/structure/TileSlider'
import { getAlternates } from '../../lib/helper/helper'

interface RegionProps {
  region: Region
  settings: any
}

const RegionPage: NextPage<RegionProps> = ({ region }: RegionProps) => {
  const {
    _id,
    name,
    nameDescription,
    h1,
    coverImage,
    meta_title,
    meta_description,
    description_1,
    description_2,
    description_3,
    faq,
    reviews,
    number_locations,
    number_activities,
    product_link,
  } = region

  return (
    <>
      <PageHead
        meta_title={meta_title}
        meta_description={meta_description}
        alternates={getAlternates(region)}
        coverImage={coverImage}
        wildcards={{ number_activities, number_locations }}
        item={region}
        faqs={faq}
      />
      <PageBanner
        coverImage={coverImage}
        name={name}
        nameDescription={nameDescription}
        h1={h1}
        wildcards={{ number_activities, number_locations }}
        product_link={product_link}
      />
      <TileSlider
        labelKey={'cities-in-region'}
        location={name}
        loadWith={getGetAllCitiesInRegion(_id)}
      />
      <TileSlider
        labelKey={'top-products-in-region'}
        location={name}
        loadWith={getGetAllProductsInLocation('region', _id)}
        type={'product'}
      />
      <TileSlider
        labelKey={'categories-in-region'}
        location={name}
        keepLocation
        loadWith={getGetAllCategoriesInRegion(_id)}
        type={'category'}
      />
      {!!faq?.length && faq !== null && <FAQ faqs={faq} nameEntity={name} />}
      {description_1 && !!description_1.length && (
        <Description
          description={description_1}
          withBorder
          nameLocation={name}
          {...reviews}
        />
      )}
      {description_2 && !!description_2.length && (
        <Description description={description_2} withBorder />
      )}
      {description_3 && !!description_3.length && (
        <Description description={description_3} withBorder />
      )}
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, preview, locale } = context
  const { region: regionSlug } = params
  const client = getClient(preview ? { token: readToken } : undefined)
  const [region, translations] = await Promise.all([
    getRegionBySlug(locale as AppLocale, client, regionSlug as string),
    serverSideTranslations(locale, ['common']),
  ])

  if (!region) {
    return {
      notFound: true,
    }
  }

  return {
    props: {
      ...translations,
      region,
    },
  }
}

export default RegionPage
